// Venue-aware planner UI surface.
//
// Replaces the abstract-amperage enumeration with a concrete load-in
// recommendation against a specific target venue's feed inventory.
// Operator picks the target venue (defaults to the current event's venue),
// then sees three strategy-tuned recommendations side-by-side: Safest
// (70% per feed) / Balanced (80%) / Aggressive (90%). Per spatial partition,
// each strategy renders which physical feed each service goes on, with
// proximity tier and utilization.

import { useEffect, useMemo, useState } from "react";
import { listVenues, getVenueHouseFeeds, ApiError } from "@/lib/api";
import type { VenueHouseFeed } from "@/lib/api";
import type { SuggestionsServiceInput, Venue } from "@/lib/types";
import { planVenue, type ProximityTier, type VenuePlan } from "@/lib/venuePlanner";
import {
  useAggressivePlanCoincidence,
  assignmentKey,
  type AssignmentCoincidenceMap,
} from "@/hooks/useAggressivePlanCoincidence";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FeedDetailDialog } from "@/components/FeedDetailDialog";
import { cn } from "@/lib/utils";

interface StrategyCfg {
  key: "safest" | "balanced" | "aggressive";
  label: string;
  utilization: number;
  description: string;
  /** Placement-time multiplier: Aggressive packs ~30% beyond
   *  sum-of-show_condition_max anticipating diversity benefit
   *  revealed by the Tier 1 coincidence overlay. */
  diversityMultiplier: number;
  /** Strict columns refuse to place services on undeclared feeds
   *  (catwalks reserved for rigging, broadcast for TV crews, etc.).
   *  Anything that doesn't fit on a declared feed lands in `unplaced`
   *  so the operator sees a red warning rather than a quiet 'far' tag.
   *  Aggressive stays permissive so the operator can use it as the
   *  'what would the planner do if we let it auto-fall-back' view. */
  strictNoFallback: boolean;
  primary?: boolean;
}

const STRATEGIES: StrategyCfg[] = [
  { key: "safest", label: "Safest", utilization: 0.7, description: "70% per feed · declared feeds only", diversityMultiplier: 1.0, strictNoFallback: true },
  { key: "balanced", label: "Balanced", utilization: 0.8, description: "80% per feed · declared feeds only", diversityMultiplier: 1.0, strictNoFallback: true, primary: true },
  { key: "aggressive", label: "Aggressive", utilization: 0.9, description: "90% per feed · packs with diversity · fallback OK", diversityMultiplier: 1.3, strictNoFallback: false },
];

// Proximity labels + tones map the 4-tier bucket AND the affinity score
// to a single inline hint. Within "adjacent" we still distinguish stronger
// matches ("close": content + directional) from weaker ones ("nearby":
// content only) and from acronym-only matches ("loose"). This is the
// visible expression of the planner's ranked-distance ordering.
function proximityLabel(prox: ProximityTier, affinity: number): string {
  if (prox === "assigned") return "assigned";
  if (prox === "exact") return "same location";
  if (prox === "fallback") {
    // Fallback affinity 0.5 = declared service feed (shared) — overflow
    // landed on a feed the operator declared for some other touring
    // identity. Better than landing on a truly-undeclared feed (catwalk).
    return affinity >= 0.5 ? "shared" : "far";
  }
  if (affinity >= 4) return "close";
  if (affinity >= 3) return "nearby";
  return "loose match";
}
function proximityTone(prox: ProximityTier, affinity: number): string {
  if (prox === "assigned") return "text-emerald-400 font-medium";
  if (prox === "exact") return "text-emerald-400";
  if (prox === "fallback") {
    return affinity >= 0.5 ? "text-amber-500/80" : "text-red-400";
  }
  if (affinity >= 4) return "text-emerald-300";
  if (affinity >= 3) return "text-amber-300";
  return "text-amber-500/80";
}

function fmtUtil(u: number): string {
  return `${Math.round(u * 100)}%`;
}

// Render the feed identity cleanly. Operator-typed feed_name often carries
// an outdated amperage suffix (e.g., "FOH 200A" but capacity_amps=250) which
// creates confusion when we then display the real capacity beside it. Strip
// the trailing "<number>A" pattern from feed_name so we present a single
// authoritative amperage (capacity_amps).
function fmtFeedLabel(feed_name: string, capacity_amps: number): string {
  const stripped = feed_name.replace(/\s*\d+\s*A\b\s*$/i, "").trim();
  const base = stripped || feed_name;
  return `${base} · ${capacity_amps}A`;
}

// Stable identity for the open drill-down dialog. null = no dialog open.
interface OpenDetail {
  feedId: string;
  feedName: string;
  feedRatingA: number;
  membershipIds: string[];
  serviceNames: string[];
}

export function VenuePlannerSection({
  windowId,
  sourceVenueId,
  services,
}: {
  windowId: string;
  sourceVenueId: string | null;
  services: SuggestionsServiceInput[];
}) {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [venuesError, setVenuesError] = useState<string | null>(null);
  const [targetVenueId, setTargetVenueId] = useState<string | null>(sourceVenueId);
  const [feeds, setFeeds] = useState<VenueHouseFeed[] | null>(null);
  const [feedsError, setFeedsError] = useState<string | null>(null);
  const [loadingFeeds, setLoadingFeeds] = useState(false);

  // Reset target when the event changes underneath us (different source venue).
  useEffect(() => {
    setTargetVenueId(sourceVenueId);
  }, [sourceVenueId]);

  // Load venue list once.
  useEffect(() => {
    listVenues()
      .then((d) => {
        setVenues(d.venues.filter((v) => v.deleted_at == null));
      })
      .catch((e: Error) => {
        const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e.message;
        setVenuesError(msg);
      });
  }, []);

  // Load feeds for the selected target venue.
  useEffect(() => {
    if (!targetVenueId) {
      setFeeds(null);
      return;
    }
    setLoadingFeeds(true);
    setFeedsError(null);
    getVenueHouseFeeds(targetVenueId)
      .then((d) => setFeeds(d.feeds))
      .catch((e: Error) => {
        const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e.message;
        setFeedsError(msg);
        setFeeds(null);
      })
      .finally(() => setLoadingFeeds(false));
  }, [targetVenueId]);

  // Compute the 3 plans whenever (services, feeds) change. served_identities
  // travels on each feed object, so the planner's 'assigned' tier picks
  // them up without any per-venue threading here.
  const plans = useMemo(() => {
    if (!feeds) return null;
    const out: Record<string, VenuePlan> = {};
    for (const s of STRATEGIES) {
      out[s.key] = planVenue({
        services,
        feeds,
        utilizationTarget: s.utilization,
        diversityMultiplier: s.diversityMultiplier,
        strictNoFallback: s.strictNoFallback,
      });
    }
    return out;
  }, [services, feeds]);

  const isSourceVenue = targetVenueId === sourceVenueId;

  // Tier 1 + Tier 2 both consume SOURCE-event telemetry keyed by
  // membership_id — neither depends on whether the target venue is the
  // source venue or some future hypothetical. The set of membership_ids
  // is the same regardless of target venue; only the per-feed assignment
  // shape changes. Enable both for every target so the operator can
  // compare combined load profiles across venues.
  const aggressiveCoincidence = useAggressivePlanCoincidence(
    windowId,
    plans?.aggressive ?? null,
    /* enabled */ true,
  );

  const [openDetail, setOpenDetail] = useState<OpenDetail | null>(null);

  return (
    <Card className="border-emerald-500/40">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <CardTitle className="text-base">Recommended load-in plan</CardTitle>
          {isSourceVenue ? (
            <span className="text-xs text-muted-foreground">
              (this event's venue — compare to the Audit tab for current bookings)
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">
              (planning a future event at a different venue)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 pt-2">
          <label className="text-xs text-muted-foreground">Target venue:</label>
          <select
            value={targetVenueId ?? ""}
            onChange={(e) => setTargetVenueId(e.target.value || null)}
            className="bg-background border border-border rounded px-2 py-1 text-sm flex-1 max-w-md"
          >
            <option value="">— pick a venue —</option>
            {venues.map((v) => (
              <option key={v.venue_id} value={v.venue_id}>
                {v.name}{v.venue_id === sourceVenueId ? " (this event)" : ""}
              </option>
            ))}
          </select>
        </div>
        {venuesError && (
          <p className="text-xs text-red-400 mt-1">Venues: {venuesError}</p>
        )}
      </CardHeader>
      <CardContent>
        {!targetVenueId && (
          <p className="text-sm text-muted-foreground">
            Pick a target venue to see a recommended load-in plan.
          </p>
        )}
        {targetVenueId && loadingFeeds && (
          <p className="text-sm text-muted-foreground">Loading venue feeds…</p>
        )}
        {feedsError && (
          <p className="text-sm text-red-400">Feeds: {feedsError}</p>
        )}
        {plans && feeds && (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">
              Venue inventory: <strong>{feeds.length}</strong> active feeds.
              Each strategy tries to balance load across the nearest feeds.
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              {STRATEGIES.map((s) => (
                <StrategyPlanColumn
                  key={s.key}
                  cfg={s}
                  plan={plans[s.key]}
                  coincidence={s.key === "aggressive" ? aggressiveCoincidence : null}
                  onDrillDown={setOpenDetail}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
      {openDetail && (
        <FeedDetailDialog
          open={openDetail != null}
          onClose={() => setOpenDetail(null)}
          windowId={windowId}
          membershipIds={openDetail.membershipIds}
          feedRatingA={openDetail.feedRatingA}
          feedName={openDetail.feedName}
          serviceNames={openDetail.serviceNames}
          venueTz={venues.find((v) => v.venue_id === targetVenueId)?.timezone ?? null}
        />
      )}
    </Card>
  );
}

// Feed-centric view of a VenuePlan: one row per unique feed used, with
// services grouped by their touring identity inside the row. Replaces the
// prior partition-centric rendering where shared feeds appeared twice
// (once per partition). Operator's mental model is "look at each breaker
// → see what's on it", which is what this layout supports.
interface FeedRow {
  feed: VenueHouseFeed;
  proximity: ProximityTier;
  affinity: number;
  feed_sum_amps_total: number;
  // Per touring identity contributing services to this feed.
  groups: Array<{
    touring_identity: string | null;
    tier: string | null;
    services: SuggestionsServiceInput[];
    sum_amps: number;
  }>;
}

function tierRank(t: ProximityTier): number {
  return t === "assigned" ? 0 : t === "exact" ? 1 : t === "adjacent" ? 2 : 3;
}

function gatherFeedRows(plan: VenuePlan): FeedRow[] {
  const rows = new Map<string, FeedRow>();
  for (const partition of plan.partitions) {
    for (const a of partition.assignments) {
      let row = rows.get(a.feed.feed_id);
      if (!row) {
        row = {
          feed: a.feed,
          proximity: a.proximity,
          affinity: a.affinity,
          // sum_amps_total is global across partitions; same value on
          // every assignment for this feed, so first-seen is correct.
          feed_sum_amps_total: a.feed_sum_amps_total,
          groups: [],
        };
        rows.set(a.feed.feed_id, row);
      } else {
        // Same feed seen from another partition — keep the BEST proximity
        // / affinity (the strongest reason this feed got picked anywhere).
        if (tierRank(a.proximity) < tierRank(row.proximity) ||
            (tierRank(a.proximity) === tierRank(row.proximity) && a.affinity > row.affinity)) {
          row.proximity = a.proximity;
          row.affinity = a.affinity;
        }
      }
      row.groups.push({
        touring_identity: partition.touring_identity,
        tier: partition.tier,
        services: a.services,
        sum_amps: a.sum_amps,
      });
    }
  }
  // Sort: best tier first (assigned > exact > adjacent > fallback), then
  // by combined load descending so the heaviest feeds render first inside
  // each tier.
  return [...rows.values()].sort((a, b) => {
    const tDelta = tierRank(a.proximity) - tierRank(b.proximity);
    if (tDelta !== 0) return tDelta;
    return b.feed_sum_amps_total - a.feed_sum_amps_total;
  });
}

interface UnplacedRow {
  touring_identity: string | null;
  tier: string | null;
  service: SuggestionsServiceInput;
}

function gatherUnplaced(plan: VenuePlan): UnplacedRow[] {
  const out: UnplacedRow[] = [];
  for (const partition of plan.partitions) {
    for (const s of partition.unplaced) {
      out.push({
        touring_identity: partition.touring_identity,
        tier: partition.tier,
        service: s,
      });
    }
  }
  return out;
}

function StrategyPlanColumn({
  cfg, plan, coincidence, onDrillDown,
}: {
  cfg: StrategyCfg;
  plan: VenuePlan;
  coincidence: AssignmentCoincidenceMap | null;
  onDrillDown: ((d: OpenDetail) => void) | null;
}) {
  const feedRows = useMemo(() => gatherFeedRows(plan), [plan]);
  const unplaced = useMemo(() => gatherUnplaced(plan), [plan]);
  const totalFeedsUsed = feedRows.length;
  const totalUnplaced = unplaced.length;
  return (
    <Card className={cn(cfg.primary && "border-emerald-500/60")}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          {cfg.label}{" "}
          <span className="text-xs text-muted-foreground font-normal">
            {cfg.description}
          </span>
        </CardTitle>
        <div className="text-xs text-muted-foreground">
          {totalFeedsUsed} feed{totalFeedsUsed === 1 ? "" : "s"} used
          {plan.unused_feeds.length > 0 && (
            <> · {plan.unused_feeds.length} unused</>
          )}
          {totalUnplaced > 0 && (
            <span className="text-red-400">
              {" "}· {totalUnplaced} service{totalUnplaced === 1 ? "" : "s"} can't fit
            </span>
          )}
        </div>
        {plan.strict_auto_disabled && (
          <div className="mt-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-200">
            Strict mode off — no feeds at this venue have served_identities
            declared yet. Showing a permissive plan from label heuristics.
            Go to{" "}
            <span className="font-mono">/admin/venues</span>{" "}
            → expand this venue → House feeds → Edit a bucket → toggle
            Touring identities served, to enable strict Safest/Balanced
            planning.
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {feedRows.map((row) => {
          // All services on this feed across every contributing partition.
          // Drives the Tier 1 coincidence lookup (Aggressive only) AND
          // the Tier 2 drill-down membership_ids list — operator clicking
          // the feed sees the combined load chart of every service on it.
          const allServices = row.groups.flatMap((g) => g.services);
          const mids = allServices
            .map((s) => s.roster_membership_id)
            .filter((m): m is string => !!m);
          const cKey = mids.length >= 2
            ? assignmentKey(row.feed.feed_id, mids) : null;
          const cEntry = (cKey && coincidence) ? coincidence[cKey] : undefined;
          const cAmps = (cEntry?.status === "ready")
            ? cEntry.result.coincidence_peak_a : null;
          // Safest/Balanced never apply coincidence reduction (cAmps is
          // null because Aggressive is the only column wired into the
          // coincidence overlay). Display sum-of-show-max across ALL
          // contributing partitions — the true combined breaker load.
          const displayAmps = cAmps ?? row.feed_sum_amps_total;
          const displayUtil = displayAmps / row.feed.capacity_amps;
          const canDrill = onDrillDown != null && mids.length > 0;
          const isShared = row.groups.length > 1;
          return (
            <div key={row.feed.feed_id} className={cn(
              "space-y-0.5",
              isShared && "border-l-2 border-amber-500/40 pl-2",
            )}>
              <div className="flex items-baseline gap-2">
                {canDrill ? (
                  <button
                    type="button"
                    className="font-medium underline decoration-dotted decoration-muted-foreground/40 hover:decoration-foreground text-left"
                    title="Open Tier 2 drill-down — combined load of all services on this feed"
                    onClick={() => onDrillDown!({
                      feedId: row.feed.feed_id,
                      feedName: fmtFeedLabel(row.feed.feed_name, row.feed.capacity_amps),
                      feedRatingA: row.feed.capacity_amps,
                      membershipIds: mids,
                      serviceNames: allServices.map((s) => s.service_name ?? s.system_id),
                    })}
                  >
                    {fmtFeedLabel(row.feed.feed_name, row.feed.capacity_amps)}
                  </button>
                ) : (
                  <span className="font-medium">
                    {fmtFeedLabel(row.feed.feed_name, row.feed.capacity_amps)}
                  </span>
                )}
                <span className={cn(
                  "text-xs ml-auto",
                  displayUtil > cfg.utilization ? "text-red-400"
                  : displayUtil > cfg.utilization * 0.9 ? "text-amber-400"
                  : "text-emerald-400",
                )}>
                  {fmtUtil(displayUtil)} ({displayAmps.toFixed(0)}A
                  {cAmps != null && (
                    <span className="text-muted-foreground">
                      {" "}coincident · sum {row.feed_sum_amps_total.toFixed(0)}A
                    </span>
                  )}
                  {cEntry?.status === "loading" && (
                    <span className="text-muted-foreground italic">
                      {" "}· computing coincidence…
                    </span>
                  )}
                  )
                </span>
              </div>
              <div className={cn("text-[10px]", proximityTone(row.proximity, row.affinity))}>
                {row.feed.location_label ?? "no location"} · {proximityLabel(row.proximity, row.affinity)}
                {isShared && (
                  <span className="ml-1 text-amber-300">· shared</span>
                )}
              </div>
              <ul className="text-xs text-muted-foreground pl-3 space-y-0.5">
                {row.groups.map((g, gIdx) => (
                  <li key={gIdx}>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
                      {g.touring_identity ?? "untagged"}
                      {g.tier && (
                        <span className="text-muted-foreground/50"> · {g.tier}</span>
                      )}
                      <span className="text-muted-foreground/70 ml-1 normal-case tracking-normal">
                        ({g.sum_amps.toFixed(0)}A)
                      </span>
                    </span>
                    <span className="ml-2">
                      {g.services.map((s) => s.service_name ?? s.system_id).join(", ")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
        {feedRows.length === 0 && !plan.strict_auto_disabled && totalUnplaced === 0 && (
          <div className="text-xs text-muted-foreground py-2 italic">
            No services to place.
          </div>
        )}
        {unplaced.length > 0 && (
          <div className="pt-2 border-t border-border/40 space-y-1">
            <div className="text-xs uppercase tracking-wide text-red-400">
              ⚠ Unplaced ({unplaced.length})
            </div>
            <ul className="text-xs text-red-400/80 pl-3 list-disc">
              {unplaced.map((u, i) => (
                <li key={i}>
                  {u.service.service_name ?? u.service.system_id}
                  <span className="text-muted-foreground/70">
                    {" "}· {u.touring_identity ?? "untagged"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
